import { ColumnDef } from '@tanstack/react-table';
import React, { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Plus } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { DataTable, DataTableColumnHeader } from '@ui/components/ui/data-table';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { cn } from '@ui/lib/utils';
import { Loader } from '@ui';

import { ClearFilterButton, FilterField } from '@app/components/core';
import { memberPath } from '@app/constants/url';
import { filterBarClass, pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { useOrganizationId } from '@app/utils/hooks';
import { Roles } from '@app/utils/types';
import { MembershipEntity, MembershipStatus, PmMembersParams } from '@data';
import { useListPmMembers } from '@data/hooks/membership';
import { useOrganizations } from '@data/hooks/organizations';

import { MembersProps } from '../MembersRouter';
import AddMemberDialog from './AddMemberDialog';

type MembersListRow = {
  id: string;
  member: string;
  employeeNumber: string;
  defaultChapterLabel: string;
  permissions: string;
  status?: MembershipStatus;
};

const dash = <span className="text-muted-foreground">-</span>;

function MembersListLoaded({ user, organizationId }: MembersProps & { organizationId: string }): ReactElement {
  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();
  // The signed-in person's role in this organization, from the same source `withAuth` gates routes on.
  const { data: organizations } = useOrganizations(user.id);
  const isAdmin = organizations?.items?.find(o => o.id === organizationId)?.role === Roles.admin;
  const [isAdding, setIsAdding] = useState(false);

  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  // Opens on the people who still work here. Somebody who left is found by asking for them, not by
  // scrolling past them.
  const [statusValue, setStatusValue] = useState<string[]>([MembershipStatus.active]);

  // The API takes a single status, so when neither or both are picked we fetch everything and narrow here.
  const membersPmParams: PmMembersParams = {
    userStatus: statusValue.length === 1 ? (statusValue[0] as MembershipStatus) : undefined,
  };
  const { data: pmMembers, isLoading: pmMembersIsLoading } = useListPmMembers(organizationId, membersPmParams);

  const rows = useMemo<MembersListRow[]>(() => {
    const inStatus = (status?: MembershipStatus): boolean =>
      !statusValue.length || (!!status && statusValue.includes(status));

    return (pmMembers?.items ?? [])
      .filter(x => x.role !== Roles.client && inStatus(x.user.status))
      .map(
        (x: MembershipEntity): MembersListRow => ({
          id: x.id,
          member: `${x.user.firstName || ''} ${x.user.lastName || ''}`.trim(),
          employeeNumber: x.memberNumber || '',
          // A default pointing at a chapter that has been retired reads as none rather than as a dead value.
          defaultChapterLabel: x.defaultChapter?.isActive ? x.defaultChapter.label : '',
          permissions: t(`roles.${x.role}`),
          status: x.user.status,
        }),
      );
  }, [pmMembers, statusValue, t]);

  // The same forward transition every other list→detail in the app uses; these two were the only ones
  // still jumping.
  const openMember = (row: MembersListRow): void => transitionNavigate(memberPath(organizationId, row.id), 'forward');

  // The name picker lists the rows the status filter already allows; it only narrows what is shown.
  const nameOptions = useMemo(() => rows.map(r => ({ value: r.id, label: r.member })), [rows]);
  const visibleRows = useMemo(
    () => (selectedMemberIds.length ? rows.filter(r => selectedMemberIds.includes(r.id)) : rows),
    [rows, selectedMemberIds],
  );

  /* eslint-disable react/no-unstable-nested-components -- TanStack Table column render props */
  const columns = useMemo<ColumnDef<MembersListRow>[]>(
    () => [
      {
        accessorKey: 'member',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('member')} />,
        cell: ({ row }) => <span className="flex min-w-[8rem] items-center font-medium">{row.original.member}</span>,
      },
      {
        accessorKey: 'employeeNumber',
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('employeeNumber')} />,
        cell: ({ row }) => row.original.employeeNumber || dash,
      },
      {
        accessorKey: 'defaultChapterLabel',
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('defaultChapter')} />,
        cell: ({ row }) => row.original.defaultChapterLabel || dash,
      },
      {
        accessorKey: 'permissions',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('permissions')} />,
      },
      {
        id: 'chevron',
        enableSorting: false,
        header: '',
        meta: { className: 'w-0 text-right' },
        cell: () => <ChevronRight className="size-4 text-muted-foreground" />,
      },
    ],
    [t],
  );
  /* eslint-enable react/no-unstable-nested-components */

  const statusIsDefault = statusValue.length === 1 && statusValue[0] === MembershipStatus.active;
  const filtersActive = selectedMemberIds.length > 0 || !statusIsDefault;
  const clearFilters = (): void => {
    setSelectedMemberIds([]);
    setStatusValue([MembershipStatus.active]);
  };

  return (
    <>
      <div className={`${pageHeaderRowClass} mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between`}>
        <h1 className={pageTitleClass}>{t('titles.members')}</h1>
        {isAdmin && (
          <Button type="button" size="sm" onClick={() => setIsAdding(true)}>
            <Plus className="size-4" />
            {t('addMember.submit')}
          </Button>
        )}
      </div>

      <div className={`mb-6 ${filterBarClass}`}>
        <FilterField wide>
          <MultiSelectField
            label={t('titles.members')}
            options={nameOptions}
            value={selectedMemberIds}
            onChange={setSelectedMemberIds}
            emptyLabel={t('allMembers')}
            searchPlaceholder={t('search')}
            emptySearchLabel={t('noMatches')}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>

        <FilterField>
          <MultiSelectField
            label={t('status')}
            options={[
              { value: MembershipStatus.active, label: t('tabs.active') },
              { value: MembershipStatus.deactivated, label: t('tabs.deactivated') },
            ]}
            value={statusValue}
            onChange={setStatusValue}
            emptyLabel={t('allStatuses')}
            searchable={false}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>

        {filtersActive && <ClearFilterButton onClick={clearFilters} />}
      </div>

      <div className="mb-6">
        <div className="hidden md:block">
          <DataTable<MembersListRow>
            columns={columns}
            data={visibleRows}
            isLoading={pmMembersIsLoading}
            initialSorting={[{ id: 'member', desc: false }]}
            getRowId={row => row.id}
            onRowClick={openMember}
            // A deactivated member's whole row goes quiet. With both statuses on screen at once it is the
            // only cue, and it reads calmer than a tag on the name; their own page says it outright.
            // `text-inherit` on the descendants because a cell left at full strength next to a faded name
            // reads as a rendering slip rather than a state.
            rowClassName={row =>
              row.status === MembershipStatus.deactivated ? 'text-muted-foreground/55 [&_*]:text-inherit' : undefined
            }
            padded
          />
        </div>
        {/* Rendered from the same `rows` as the table: a card is now a way into a person, so it has to
            carry what the columns carry and honour the status filter the table honours. */}
        <div className="md:hidden">
          {/* One list across the screen, a row per person, rather than a stack of inset cards. */}
          <div className="-mx-4 flex flex-col divide-y divide-border border-y border-border">
            {visibleRows.map(row => (
              <button
                key={row.id}
                type="button"
                onClick={() => openMember(row)}
                className={cn(
                  'flex items-center gap-3 bg-card p-4 text-left text-card-foreground transition-colors hover:bg-accent',
                  row.status === MembershipStatus.deactivated && 'text-muted-foreground/55 [&_*]:text-inherit',
                )}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <h3 className="truncate text-lg font-bold text-foreground">{row.member}</h3>
                  <p className="text-sm text-foreground">{`${t('permissions')}: ${row.permissions}`}</p>
                  {row.employeeNumber ? (
                    <p className="text-sm text-foreground">{`${t('employeeNumber')}: ${row.employeeNumber}`}</p>
                  ) : null}
                  {row.defaultChapterLabel ? (
                    <p className="text-sm text-foreground">{`${t('defaultChapter')}: ${row.defaultChapterLabel}`}</p>
                  ) : null}
                </div>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              </button>
            ))}
          </div>
        </div>
      </div>

      <AddMemberDialog
        organizationId={organizationId}
        open={isAdding}
        onClose={() => setIsAdding(false)}
        canGrantAnyRole={isAdmin}
      />
    </>
  );
}

const MembersList = ({ user }: MembersProps): ReactElement => {
  const organizationId = useOrganizationId();
  if (!organizationId) {
    return <Loader />;
  }
  return <MembersListLoaded user={user} organizationId={organizationId} />;
};

export default MembersList;
